import React from 'react';
import styled from '@benzinga/themetron';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';
import { Profile } from '@benzinga/content-manager';
import { Grid, Button, Icon } from '@benzinga/core-ui';

import { EntityAttributeDisplay } from '@benzinga/entity-ui';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { AddToWatchlist } from '@benzinga/watchlist-ui';
import { BzImage } from '@benzinga/image';
import { Blocks } from '@benzinga/blocks';

export interface ProfileHeaderProps {
  profile: Profile;
}

const ActionButtons = ({ profile }) => {
  return (
    <ButtonsWrapper>
      {profile.ticker && <AddToWatchlist symbol={profile.ticker} />}
      {profile.entity.status === 'verified' && <VerifiedButton />}
      {profile.entity.affiliate_link && (
        <a href={profile.entity.affiliate_link} rel="nofollow noreferrer" target="_blank">
          <Button className="get-started" variant="warning">
            <Icon className="link-icon" icon={faLink} /> <span className="text">Get Started</span>
          </Button>
        </a>
      )}
    </ButtonsWrapper>
  );
};

const VerifiedButton = () => (
  <Button className="verified" variant="success">
    <Icon icon={faShieldCheck} />
    <span className="text">Verified</span>
  </Button>
);

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  @media screen and (max-width: 800px) {
    justify-content: center;
  }
  .get-started,
  .verified {
    font-size: 1.2rem;
  }
  .text {
    margin-left: 0.2rem;
    font-size: 0.9rem;
  }
`;

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ profile }) => {
  return (
    <ProfileHeaderContainer className="profile-cover-section">
      {profile.layout === 'custom' ? (
        <Blocks blocks={profile.left_header} />
      ) : (
        <PlatformInfo
          affiliateLinkCompany={profile?.entity?.affiliate_link}
          image={profile.entity.image}
          name={profile.entity?.name}
        />
      )}
      <ProfileHighlights profile={profile} />
    </ProfileHeaderContainer>
  );
};

export interface ProfileHighlightsProps {
  profile: Profile;
}

export const ProfileHighlights: React.FC<ProfileHighlightsProps> = ({ profile }) => {
  return (
    <>
      {profile?.highlights && profile.highlights.length > 0 && (
        <Grid className="profile-highlights">
          {profile.highlights.map(highlight => {
            return <EntityAttributeDisplay attribute={highlight} />;
          })}
        </Grid>
      )}
    </>
  );
};

const ProfileHeaderContainer = styled.section`
  background: #ffffff;
  border-radius: 5px;
  width: 100%;

  .profile-highlights {
    padding: 1rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    @media screen and (max-width: 800px) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
    .entity-attr.featured {
      .label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: bold;
      }
    }
  }

  /* .profile-title-wrapper {
    margin-left: 1rem;
  } */

  .profile-details-container {
    align-items: center;
    background: #ffffff;
    border-radius: 0.25rem;
    border: 1px solid #ebebeb;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    padding: 1rem;
    width: 100%;
  }

  .call-to-action-link {
    margin-left: auto;
    background: #e8743e;
    color: ${({ theme }) => theme.colorPalette.white};
  }

  @media screen and (max-width: 800px) {
    height: auto;
    .profile-details-container {
      flex-direction: column;
      align-items: baseline;
    }
    .profile-name {
      margin: 0;
    }
    .profile-details-info-1 {
      flex-direction: column;
      align-items: center;
      .call-to-action-link {
        width: 100%;
        margin-top: 12px;
      }
    }
  }

  @media screen and (min-width: 800px) {
    .call-to-action-link {
      margin-left: 40px;
    }
  }
  .local-container {
    padding-right: 15px;
    padding-left: 15px;
    box-sizing: border-box;

    @media screen and (max-width: 800px) {
      text-align: center;
      margin-right: auto;
      margin-left: auto;
      .profile-title-wrapper {
        margin: 0;
        margin-top: 0.5rem;
      }
    }
  }

  .profile-details-info-1 {
    display: flex;
    flex-direction: column;
    width: 100%;

    @media screen and (min-width: 800px) {
      flex-direction: column;
      align-items: center;
    }
  }

  .profile-logo {
    width: 150px;
    height: 100px;
    background-repeat: no-repeat;
    background-size: contain;
    display: inline-block;
    transition: all 0.15s ease-in-out;
    border-radius: unset;
    background-position: center;
  }

  .profile-name {
    display: flex;
    align-items: center;
    font-size: 2rem;
    font-weight: bold;

    a {
      color: ${({ theme }) => theme.colorPalette.black};
    }
  }

  .call-to-action-link {
    padding: 0 15px;
    display: block;
    border-radius: 5px;
    font-size: 14px;
    height: 48px;
    align-items: center;
    display: flex;
    justify-content: center;
    white-space: nowrap;
    font-weight: 600;

    .link-icon {
      font-size: 1.1rem;
    }

    .text {
      margin-left: 3px;
    }
  }

  .verified-badge {
    width: 21px;
    height: 21px;
    margin-left: 6px;
  }

  .platform-info {
    display: flex;
    margin-bottom: 40px;
    flex-direction: column;

    @media screen and (min-width: 1050px) {
      flex-direction: row;
    }
  }

  .platform-details-container {
    @media screen and (min-width: 1050px) {
      max-width: 380px;
    }
  }
`;

export interface PlatformInfoProps {
  name: string;
  image?: string | null;
  affiliateLinkCompany?: string | null;
}

export const PlatformInfo: React.FC<PlatformInfoProps> = ({ affiliateLinkCompany, image, name }) => {
  return (
    <PlatformInfoWrapper className="platform-info-container">
      <div className="platform-info-wrapper 1212">
        {image && affiliateLinkCompany ? (
          <a href={affiliateLinkCompany}>
            <BzImage className="platform-logo" height={90} layout="fixed" src={image} width="100%" />
          </a>
        ) : image ? (
          <BzImage className="platform-logo" height={90} layout="fixed" src={image} width="100%" />
        ) : null}
        <h1 className="listing-name">
          <a href={affiliateLinkCompany ?? '#'} rel="nofollow noreferrer" target="_blank">
            {name}
          </a>
        </h1>
      </div>
      {affiliateLinkCompany && (
        <a
          className="get-started-button"
          data-label="Offering Get Started"
          href={affiliateLinkCompany}
          rel="noopener noreferrer"
          target="_blank"
        >
          Get Started <Icon icon={faArrowRight} />
        </a>
      )}
    </PlatformInfoWrapper>
  );
};

export const PlatformInfoWrapper = styled.div`
  &.platform-info-container {
    background: #ffffff;
    margin: 1rem 0;
    border: 1px solid #ebebeb;
    border-radius: ${({ theme }) => theme.borderRadius.default};
    overflow: hidden;
    @media (max-width: 480px) {
      display: flex;
      flex-direction: column;
    }

    .listing-name {
      a {
        color: black;
      }
    }

    .platform-info-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      border-radius: 5px;
      padding: 1rem;
      width: 100%;
      .platform-logo {
        margin-bottom: 0.5rem;
      }
      .call-to-action-link {
        width: 100%;
        height: 45px;
        .link-icon {
          margin-right: 0.5rem;
          font-size: ${({ theme }) => theme.fontSize.lg};
        }
      }
    }
    .get-started-button {
      display: flex;
      background-color: ${({ theme }) => theme.colorPalette.blue500};
      padding: 10px;
      justify-content: space-between;
      align-items: center;
      color: #ffffff;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      background-color: #fa8418;

      @media (min-width: 800px) {
        white-space: nowrap;
        gap: 0.5rem;
        width: 100%;
      }
      @media (max-width: 800px) {
        width: 30%;
      }
      @media (max-width: 480px) {
        width: 100%;
      }
    }
  }
`;
